import { Suspense } from "react";
import { Column } from "@hightouch/htui";
import { ErrorBoundary } from "react-error-boundary";

import { useUser } from "src/contexts/user-context";
import {
  NewPage,
  PageCardContent,
  PageCardHeader,
} from "src/components/layout/new-page";
import { Header } from "src/pages/home/components/header";
import { ResourceCards } from "src/pages/home/components/resource-cards";
import { RecentlyViewed } from "src/pages/home/components/recently-viewed";
import { MAX_WIDTH } from "src/pages/home/constants";
import { SkeletonItem } from "src/components/skeletons";
import { lazyNamed } from "src/utils/lazy-named";

const SyncHealthCharts = lazyNamed(
  () => import("./sync-health-charts"),
  "SyncHealthCharts",
);
const SyncHealthTable = lazyNamed(
  () => import("./sync-health-table"),
  "SyncHealthTable",
);
const SyncHealthTableLegacy = lazyNamed(
  () => import("./sync-health-table-legacy"),
  "SyncHealthTableLegacy",
);

export const ActivationHome = ({
  onToggleView,
}: {
  onToggleView: () => void;
}) => {
  const { workspace } = useUser();
  const alertingV2Enabled = Boolean(workspace?.alerting_v2_enabled);

  const header = (
    <Header view="activation" onToggleView={onToggleView}>
      <ResourceCards
        resources={["model", "destination", "sync"]}
        view="activation"
      />
    </Header>
  );

  const content = (
    <>
      <RecentlyViewed view="activation" />
      <Column gap={6}>
        <ErrorBoundary fallback={null}>
          <Suspense
            fallback={
              <SkeletonItem borderRadius="lg" minHeight="300px" width="100%" />
            }
          >
            <SyncHealthCharts />
          </Suspense>
        </ErrorBoundary>
        <ErrorBoundary fallback={null}>
          <Suspense
            fallback={
              <SkeletonItem borderRadius="lg" minHeight="400px" width="100%" />
            }
          >
            {alertingV2Enabled ? (
              <SyncHealthTable />
            ) : (
              <SyncHealthTableLegacy />
            )}
          </Suspense>
        </ErrorBoundary>
      </Column>
    </>
  );

  return (
    <NewPage title="Home">
      <PageCardHeader removeBoxShadow>{header}</PageCardHeader>
      <PageCardContent hasGradient variant="light">
        <Column align="center" flex={1}>
          <Column gap={6} maxW={MAX_WIDTH} w="100%">
            {content}
          </Column>
        </Column>
      </PageCardContent>
    </NewPage>
  );
};
